/**
 * Furniture persistence helpers — read/write PlacedFurniture to the Device UI store.
 *
 * Storage format (v2): fraction-based coordinates (0..1 relative to canvas).
 * Legacy format (v1): absolute pixel coords relative to a 400 px reference canvas.
 * Migration from v1 → v2 is performed automatically on load.
 */
import type { PlacedFurniture } from '../Types';
import { furnituresList } from '../furnitures-data';
import { device_ui } from 'src/stores/features/device-ui';
import { duiSaveField } from 'src/services/php-api/device_ui';
import {
	DeviceUIField,
	type DeviceUIFurniture,
	type DeviceUIFurnitureItem,
	type DeviceUIFurnitureLegacyItem,
} from 'src/types/DeviceUI';

const REFERENCE_CANVAS_SIZE = 400; // px — used only for migrating v1 data

// ── Type guards ──────────────────────────────────────────────────────────────

function isLegacyItem(item: unknown): item is DeviceUIFurnitureLegacyItem {
	return typeof item === 'object' && item !== null && typeof (item as any).refX === 'number';
}

// ── Load ─────────────────────────────────────────────────────────────────────

export function loadFurnituresDUI(deviceId: string): PlacedFurniture[] {
	try {
		const raw: DeviceUIFurniture =
			device_ui.getDeviceUIField(deviceId, DeviceUIField.FURNITURE) || [];

		return raw.map((item, i): PlacedFurniture => {
			if (isLegacyItem(item)) {
				// ── Migrate v1 → v2 ──────────────────────────────────────────
				const refX = item.refX ?? 0;
				const refY = item.refY ?? 0;
				const w = item.width ?? 40;
				const h = item.height ?? 40;

				// Extract filename from the img path
				const src = (item.img || '').split('/').pop() || 'chair1.svg';

				return {
					id: `furn-migrated-${i}-${Date.now()}`,
					src,
					name: item.name || src.replace(/\.(svg|png|jpg)$/i, ''),
					xFrac: clamp01(refX / REFERENCE_CANVAS_SIZE),
					yFrac: clamp01(refY / REFERENCE_CANVAS_SIZE),
					wFrac: clamp01(w / REFERENCE_CANVAS_SIZE),
					hFrac: clamp01(h / REFERENCE_CANVAS_SIZE),
					rotation: item.rotation ?? 0,
				};
			}

			// ── v2 item ──────────────────────────────────────────────────────
			const v2 = item as DeviceUIFurnitureItem;
			return {
				id: v2.id,
				src: v2.src,
				name: v2.name,
				xFrac: clamp01(v2.xFrac ?? 0),
				yFrac: clamp01(v2.yFrac ?? 0),
				wFrac: clamp01(Math.max(0.01, v2.wFrac ?? 0.075)),
				hFrac: clamp01(Math.max(0.01, v2.hFrac ?? 0.075)),
				rotation: v2.rotation ?? 0,
			};
		});
	} catch (err) {
		console.error('[furnitures-helper] Error loading furniture:', err);
		return [];
	}
}

// ── Save ─────────────────────────────────────────────────────────────────────

export async function saveFurnituresDUI(deviceId: string, items: PlacedFurniture[]): Promise<void> {
	try {
		const payload: DeviceUIFurnitureItem[] = items.map((f) => ({
			id: f.id,
			src: f.src,
			name: f.name,
			xFrac: f.xFrac,
			yFrac: f.yFrac,
			wFrac: f.wFrac,
			hFrac: f.hFrac,
			rotation: f.rotation,
		}));
		await duiSaveField(deviceId, DeviceUIField.FURNITURE, payload);
	} catch (err) {
		console.error('[furnitures-helper] Error saving furniture:', err);
	}
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function clamp01(v: number): number {
	return Math.max(0, Math.min(1, v));
}

/**
 * Returns default fraction sizes for a new furniture item placed from the catalog.
 * Looks up pixel dimensions in furnitures-data.ts (reference 400px canvas).
 */
export function getDefaultFracs(src: string): { wFrac: number; hFrac: number } {
	const entry = Object.values(furnituresList).find((f) => (f.img || '').split('/').pop() === src);
	if (entry) {
		return {
			wFrac: Math.max(0.02, entry.width / REFERENCE_CANVAS_SIZE),
			hFrac: Math.max(0.02, entry.height / REFERENCE_CANVAS_SIZE),
		};
	}
	// Default: 12.5% of canvas (matches reference DEFAULT_HEIGHT_PERCENT)
	return { wFrac: 0.125, hFrac: 0.125 };
}
